import { useState, useCallback } from 'react';
import { generateResearch } from '../services/api';

interface UseResearchGenerationReturn {
  generate: (brandName: string, additionalInstructions?: string) => Promise<void>;
  loading: boolean;
  error: Error | null;
  progress: string;
}

/**
 * Custom hook to manage research generation
 * Handles API calls and provides progress feedback
 */
export const useResearchGeneration = (projectId: string): UseResearchGenerationReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState('');

  const generate = useCallback(async (brandName: string, additionalInstructions?: string) => {
    if (!brandName.trim()) {
      setError(new Error('Brand name is required'));
      return;
    }

    setLoading(true);
    setError(null);
    setProgress('Starting research generation...');

    try {
      // Call the cloud function to start research generation
      await generateResearch(projectId, brandName, additionalInstructions);

      // Progress messages will be shown in the UI component
      // The actual project update will happen via real-time listener
      setProgress('Research generation started');
    } catch (err) {
      console.error('Error generating research:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate research';
      setError(new Error(errorMessage));
      setProgress('');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  return {
    generate,
    loading,
    error,
    progress,
  };
};
